/**
 * Legacy ys142857 registration form layout — used when drilling from 全球玩家(表).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedField,
  ThemedInput,
  ThemedPanel,
} from '../ui/ThemedSurface';

function fmtBal(v: string | undefined): string {
  const n = Number(v ?? '0');
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function levelDisplayAlias(code: string | undefined): string {
  const map: Record<string, string> = {
    E1: '一星 $100 ★',
    E2: '二星 $1000 ★★',
    E3: '三星 $10000 ★★★',
  };
  return map[code ?? ''] ?? code ?? '—';
}

export function memberPaymentFormula(pkg?: 'E1' | 'E2' | 'E3'): string {
  if (pkg === 'E1') return '90%RP+10%TP+0.05FC支付（一星）';
  if (pkg === 'E3') return '90%RP+10%TP+0.05FC支付（三星）';
  return '90%RP+10%TP+0.05FC支付（二星）';
}

export type RegisterStarPackage = 'E1' | 'E2' | 'E3';

export function StarPackageSelect({
  value,
  onChange,
  available,
  label,
}: {
  value: RegisterStarPackage;
  onChange: (v: RegisterStarPackage) => void;
  available: RegisterStarPackage[];
  label: string;
}) {
  return (
    <ThemedField label={label}>
      <div className="flex flex-wrap gap-2">
        {available.map((pkg) => {
          const active = value === pkg;
          return (
            <button
              key={pkg}
              type="button"
              onClick={() => onChange(pkg)}
              className="min-w-[96px] flex-1 rounded-xl border px-3 py-2.5 text-xs font-semibold transition-all active:scale-[0.98]"
              style={{
                borderColor: active ? C.gold : C.line,
                color: active ? C.gold : C.muted,
                background: active ? 'rgba(212,175,55,0.12)' : 'transparent',
              }}
            >
              {levelDisplayAlias(pkg)}
            </button>
          );
        })}
      </div>
    </ThemedField>
  );
}

export function subPaymentFormula(reinvest: 'sp1' | 'sp3'): string {
  // SP1 = 95% + 5% TP (E2: 950 SP1 + 50 TP); SP3 = 90% + 10% TP. SP2 已取消.
  if (reinvest === 'sp3') return '90%SP3+10%TP+0.05FC支付';
  return '95%SP1+5%TP+0.05FC支付';
}

export function ReadonlyFormField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <ThemedField label={label}>
      <ThemedInput readOnly value={value} className="text-[#F5EC63]" />
      {hint ? (
        <p className="mt-1 text-[11px]" style={{ color: C.muted }}>
          {hint}
        </p>
      ) : null}
    </ThemedField>
  );
}

type BalanceCell = { key: string; label: string; value: string; color: string };

export function SponsorBalanceGrid({ cells }: { cells: BalanceCell[] }) {
  return (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-max gap-2"
        style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(72px, 1fr))` }}
      >
        {cells.map((cell) => (
          <ThemedPanel key={cell.key} className="px-2 py-2 text-center">
            <div className="text-[10px]" style={{ color: C.muted }}>
              {cell.label}
            </div>
            <div className="mt-1 font-mono text-xs font-bold" style={{ color: cell.color }}>
              {fmtBal(cell.value)}
            </div>
          </ThemedPanel>
        ))}
      </div>
    </div>
  );
}

export function RegistrationAgreementRow({
  agreed,
  onAgreedChange,
}: {
  agreed: boolean;
  onAgreedChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex cursor-pointer items-center justify-end gap-2 text-[11px]" style={{ color: C.muted }}>
      <input
        type="checkbox"
        checked={agreed}
        onChange={(e) => onAgreedChange(e.target.checked)}
        className="size-4 rounded-full border accent-[#F5EC63]"
      />
      <span>
        {t('auth.agreePrefix', '我已阅读并同意')}
        <Link to="/terms" className="ml-0.5 underline" style={{ color: C.gold }}>
          {t('auth.userAgreement', '《用户协议》')}
        </Link>
      </span>
    </label>
  );
}

export function RegistrationActionButtons({
  onCancel,
  onReset,
  onSubmit,
  submitLabel,
  submitting,
  submitDisabled,
}: {
  onCancel: () => void;
  onReset?: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitting?: boolean;
  submitDisabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`grid gap-2 ${onReset ? 'grid-cols-3' : 'grid-cols-2'}`}>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg py-3 text-sm font-bold text-white"
        style={{ background: '#3d6fd8' }}
      >
        {t('registerForm.cancel', '取消注册')}
      </button>
      {onReset ? (
        <button
          type="button"
          onClick={onReset}
          className="rounded-lg py-3 text-sm font-bold text-white"
          style={{ background: '#3d6fd8' }}
        >
          {t('common.reset', '重置')}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting || submitDisabled}
        className="rounded-lg py-3 text-sm font-bold disabled:opacity-40"
        style={{ background: C.gold, color: '#1a1208' }}
      >
        {submitting ? t('auth.loggingIn', '处理中…') : submitLabel}
      </button>
    </div>
  );
}
